import type { Grid, Token, TokenColor, DifficultyTier, Puzzle, Pos, Cell, Direction } from "../types.ts";
import { mulberry32, randInt, randPick, shuffle } from "./prng.ts";
import { createEmptyGrid, addWallBetween, posToKey, getCell } from "./grid.ts";
import { applyDirection } from "./movement.ts";
import { solve } from "./solver.ts";

const ALL_COLORS: TokenColor[] = ["red", "blue", "green", "yellow"];
const DIRECTIONS: Direction[] = ["up", "down", "left", "right"];

export const DIFFICULTY_TIERS: Record<string, DifficultyTier> = {
  easy: {
    name: "easy",
    rows: 5, cols: 5,
    tokenCount: 2,
    hasHoles: false, hasTeleporters: false,
    minOptimal: 4, maxOptimal: 6,
  },
  medium: {
    name: "medium",
    rows: 6, cols: 6,
    tokenCount: 3,
    hasHoles: false, hasTeleporters: false,
    minOptimal: 5, maxOptimal: 8,
  },
  hard: {
    name: "hard",
    rows: 7, cols: 7,
    tokenCount: 3,
    hasHoles: false, hasTeleporters: true,
    minOptimal: 6, maxOptimal: 10,
  },
  expert: {
    name: "expert",
    rows: 8, cols: 8,
    tokenCount: 4,
    hasHoles: false, hasTeleporters: true,
    minOptimal: 6, maxOptimal: 12,
  },
};

export function generatePuzzle(seed: number, tier: DifficultyTier): Puzzle {
  const rng = mulberry32(seed);
  const maxAttempts = tier.tokenCount >= 4 ? 2000 : tier.tokenCount >= 3 ? 500 : 100;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const grid = generateRandomGrid(rng, tier);
    const result = scrambleFromSolved(rng, grid, tier);
    if (!result) continue;

    const { tokens, goalPositions, optimalMoves } = result;
    if (optimalMoves >= tier.minOptimal && optimalMoves <= tier.maxOptimal) {
      // Set goals on grid
      const colors = ALL_COLORS.slice(0, tier.tokenCount);
      for (let i = 0; i < tier.tokenCount; i++) {
        getCell(grid, goalPositions[i]!)!.goalColor = colors[i]!;
      }

      return {
        id: "",
        seed,
        grid,
        initialTokens: tokens,
        optimalMoves,
        maxMoves: optimalMoves + 5,
        difficulty: tier,
      };
    }
  }

  // Fallback: guaranteed solvable puzzle
  return createFallbackPuzzle(rng, tier);
}

/**
 * Backward generation: place tokens on goals, scramble with random moves,
 * then verify the optimal solution length via BFS.
 */
function scrambleFromSolved(
  rng: () => number,
  grid: Grid,
  tier: DifficultyTier
): { tokens: Token[]; goalPositions: Pos[]; optimalMoves: number } | null {
  const colors = ALL_COLORS.slice(0, tier.tokenCount);

  // Find valid cells for goals (not holes, not teleporters)
  const validCells = getAllCells(grid).filter((p) => {
    const cell = getCell(grid, p)!;
    return !cell.isHole && cell.teleporterId == null;
  });

  if (validCells.length < tier.tokenCount) return null;

  const shuffled = shuffle(rng, validCells);
  const goalPositions = shuffled.slice(0, tier.tokenCount);

  // Start with tokens on their goals (solved state)
  let tokens: Token[] = colors.map((color, i) => ({
    color,
    pos: { ...goalPositions[i]! },
    lost: false,
  }));

  // Scramble by applying random moves (more for harder tiers)
  const scrambleMoves = tier.maxOptimal * 2 + randInt(rng, 4, 10);
  let lastDir: Direction | null = null;

  for (let step = 0; step < scrambleMoves; step++) {
    // Pick a random direction (avoid immediate reversal which would cancel out)
    const candidates = DIRECTIONS.filter((d) => d !== opposite(lastDir));
    const dir = randPick(rng, candidates);

    const result = applyDirection(grid, tokens, dir);

    // Skip if any token falls in a hole (we need all tokens alive)
    if (result.anyLost) continue;

    // Skip if no token actually moved (no-op)
    const moved = result.tokens.some(
      (t, i) => t.pos.row !== tokens[i]!.pos.row || t.pos.col !== tokens[i]!.pos.col
    );
    if (!moved) continue;

    tokens = result.tokens;
    lastDir = dir;
  }

  // Check that tokens aren't already on their goals (trivially solved)
  const alreadySolved = tokens.every(
    (t, i) => t.pos.row === goalPositions[i]!.row && t.pos.col === goalPositions[i]!.col
  );
  if (alreadySolved) return null;

  // Temporarily set goals on grid for solver
  for (let i = 0; i < tier.tokenCount; i++) {
    getCell(grid, goalPositions[i]!)!.goalColor = colors[i]!;
  }

  // Find optimal solution via BFS
  const solution = solve(grid, tokens, tier.maxOptimal + 2);

  // Clean up goals (grid may be reused if this attempt fails)
  for (let i = 0; i < tier.tokenCount; i++) {
    getCell(grid, goalPositions[i]!)!.goalColor = null;
  }

  if (!solution) return null;

  return { tokens, goalPositions, optimalMoves: solution.length };
}

function opposite(dir: Direction | null): Direction | null {
  if (!dir) return null;
  switch (dir) {
    case "up": return "down";
    case "down": return "up";
    case "left": return "right";
    case "right": return "left";
  }
}

function generateRandomGrid(rng: () => number, tier: DifficultyTier): Grid {
  const grid = createEmptyGrid(tier.rows, tier.cols);

  // Add random internal walls — more walls = more interesting token stops
  const wallProb = tier.name === "easy" ? 0.15 : tier.name === "medium" ? 0.20 : 0.25;

  // Collect all internal edges
  const edges: [Pos, Pos][] = [];
  for (let r = 0; r < tier.rows; r++) {
    for (let c = 0; c < tier.cols; c++) {
      if (c < tier.cols - 1) edges.push([{ row: r, col: c }, { row: r, col: c + 1 }]);
      if (r < tier.rows - 1) edges.push([{ row: r, col: c }, { row: r + 1, col: c }]);
    }
  }

  const shuffledEdges = shuffle(rng, edges);
  let wallCount = 0;
  const maxWalls = Math.floor(edges.length * wallProb * 2);

  for (const [a, b] of shuffledEdges) {
    if (wallCount >= maxWalls) break;
    addWallBetween(grid, a!, b!);
    // Check connectivity after adding wall
    if (!isConnected(grid)) {
      // Remove wall (undo)
      removeWallBetween(grid, a!, b!);
    } else {
      wallCount++;
    }
  }

  // Add holes
  if (tier.hasHoles) {
    const holeCount = randInt(rng, 1, 2);
    const candidates = getInteriorCells(grid);
    const shuffled = shuffle(rng, candidates);
    for (let i = 0; i < holeCount && i < shuffled.length; i++) {
      const pos = shuffled[i]!;
      const cell = getCell(grid, pos)!;
      cell.isHole = true;
    }
  }

  // Add teleporters
  if (tier.hasTeleporters) {
    const available = getInteriorCells(grid).filter((p) => {
      const cell = getCell(grid, p)!;
      return !cell.isHole;
    });
    if (available.length >= 2) {
      const shuffled = shuffle(rng, available);
      const a = shuffled[0]!;
      const b = shuffled[1]!;
      const pairId = 0;
      getCell(grid, a)!.teleporterId = pairId;
      getCell(grid, b)!.teleporterId = pairId;
      grid.teleporters.push({ id: pairId, a, b });
    }
  }

  return grid;
}

function createFallbackPuzzle(_rng: () => number, tier: DifficultyTier): Puzzle {
  const grid = createEmptyGrid(tier.rows, tier.cols);
  const colors = ALL_COLORS.slice(0, Math.min(tier.tokenCount, 2));
  const tokens: Token[] = [];

  if (colors.length >= 2) {
    // Two tokens at opposite corners, goals at opposite corners
    getCell(grid, { row: 0, col: tier.cols - 1 })!.goalColor = "red";
    getCell(grid, { row: tier.rows - 1, col: tier.cols - 1 })!.goalColor = "blue";
    tokens.push(
      { color: "red", pos: { row: tier.rows - 1, col: 0 }, lost: false },
      { color: "blue", pos: { row: 0, col: 0 }, lost: false },
    );
  } else {
    getCell(grid, { row: 0, col: tier.cols - 1 })!.goalColor = "red";
    tokens.push(
      { color: "red", pos: { row: tier.rows - 1, col: 0 }, lost: false },
    );
  }

  return {
    id: "",
    seed: 0,
    grid,
    initialTokens: tokens,
    optimalMoves: 2,
    maxMoves: 7,
    difficulty: tier,
  };
}

// ---- Helpers ----

function getAllCells(grid: Grid): Pos[] {
  const cells: Pos[] = [];
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
}

function getInteriorCells(grid: Grid): Pos[] {
  const cells: Pos[] = [];
  for (let r = 1; r < grid.rows - 1; r++) {
    for (let c = 1; c < grid.cols - 1; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
}

function isConnected(grid: Grid): boolean {
  const visited = new Set<string>();
  const start: Pos = { row: 0, col: 0 };
  const queue: Pos[] = [start];
  visited.add(posToKey(start));

  const dirs: Array<{ dir: "up" | "down" | "left" | "right"; dr: number; dc: number }> = [
    { dir: "up", dr: -1, dc: 0 },
    { dir: "down", dr: 1, dc: 0 },
    { dir: "left", dr: 0, dc: -1 },
    { dir: "right", dr: 0, dc: 1 },
  ];

  while (queue.length > 0) {
    const pos = queue.shift()!;
    for (const { dir, dr, dc } of dirs) {
      if (hasWallForConnectivity(grid, pos, dir)) continue;
      const next: Pos = { row: pos.row + dr, col: pos.col + dc };
      if (!isInBoundsCheck(grid, next)) continue;
      const key = posToKey(next);
      if (visited.has(key)) continue;
      visited.add(key);
      queue.push(next);
    }
  }

  return visited.size === grid.rows * grid.cols;
}

function hasWallForConnectivity(grid: Grid, pos: Pos, dir: "up" | "down" | "left" | "right"): boolean {
  const cell = grid.cells[pos.row]?.[pos.col];
  if (!cell) return true;
  switch (dir) {
    case "up": return cell.walls.north;
    case "down": return cell.walls.south;
    case "left": return cell.walls.west;
    case "right": return cell.walls.east;
  }
}

function isInBoundsCheck(grid: Grid, pos: Pos): boolean {
  return pos.row >= 0 && pos.row < grid.rows && pos.col >= 0 && pos.col < grid.cols;
}

function removeWallBetween(grid: Grid, a: Pos, b: Pos): void {
  const dr = b.row - a.row;
  const dc = b.col - a.col;
  const cellA = getCell(grid, a);
  const cellB = getCell(grid, b);
  if (!cellA || !cellB) return;

  if (dr === 1 && dc === 0) {
    cellA.walls.south = false;
    cellB.walls.north = false;
  } else if (dr === -1 && dc === 0) {
    cellA.walls.north = false;
    cellB.walls.south = false;
  } else if (dr === 0 && dc === 1) {
    cellA.walls.east = false;
    cellB.walls.west = false;
  } else if (dr === 0 && dc === -1) {
    cellA.walls.west = false;
    cellB.walls.east = false;
  }
}
